class DateTimeFormatter {
    #locale;

    #timeZone;

    #shortFormatter;

    #fullDateFormatter;

    #fullFormatter;

    #shortDateTimeFormatter;

    #shortDateTimeZoneFormatter;

    #shortDateTimeGenericZoneFormatter;

    #shortWithWeekdayFormatter;

    #timeWithSecondsFormatter;

    constructor (locale = 'en', timeZone = undefined) {
        this.#locale = locale;
        this.#timeZone = timeZone;
        this.initializeFormatters(locale, timeZone);
    }

    initializeFormatters (locale, timeZone) {
        this.#shortFormatter = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone,
        });

        this.#fullDateFormatter = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone,
        });

        this.#fullFormatter = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            timeZoneName: 'short',
            timeZone,
        });

        this.#shortDateTimeFormatter = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone,
        });

        this.#shortDateTimeZoneFormatter = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZoneName: 'short',
            timeZone,
        });

        this.#shortDateTimeGenericZoneFormatter = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZoneName: 'shortGeneric',
            timeZone,
        });

        this.#shortWithWeekdayFormatter = new Intl.DateTimeFormat(locale, {
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone,
        });

        this.#timeWithSecondsFormatter = new Intl.DateTimeFormat(locale, {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            timeZone,
        });
    }

    get locale () {
        return this.#locale;
    }

    set locale (newLocale) {
        this.#locale = newLocale;
        this.initializeFormatters(newLocale, this.#timeZone);
    }

    get timeZone () {
        return this.#timeZone;
    }

    set timeZone (newTimeZone) {
        this.#timeZone = newTimeZone;
        this.initializeFormatters(this.#locale, newTimeZone);
    }

    formatFull ({ date, ignoreTimeZone = false }) {
        const formatter = ignoreTimeZone
            ? new Intl.DateTimeFormat(this.#locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZoneName: 'short',
            })
            : this.#fullFormatter;

        return this.#formatPartsStripYearComma(formatter, date);
    }

    formatShort ({ date, ignoreTimeZone = false }) {
        if (ignoreTimeZone) {
            return new Intl.DateTimeFormat(this.#locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            }).format(date);
        }

        return this.#shortFormatter.format(date);
    }

    formatShortDateTime ({ date, ignoreTimeZone = false }) {
        const formatter = ignoreTimeZone
            ? new Intl.DateTimeFormat(this.#locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
            })
            : this.#shortDateTimeFormatter;

        return this.#formatPartsStripYearComma(formatter, date);
    }

    formatShortDateTimeZone ({ date, ignoreTimeZone = false }) {
        const formatter = ignoreTimeZone
            ? new Intl.DateTimeFormat(this.#locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                timeZoneName: 'short',
            })
            : this.#shortDateTimeZoneFormatter;

        return this.#formatPartsStripYearComma(formatter, date);
    }

    formatShortDateTimeGenericZone ({ date, ignoreTimeZone = false }) {
        const formatter = ignoreTimeZone
            ? new Intl.DateTimeFormat(this.#locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                timeZoneName: 'shortGeneric',
            })
            : this.#shortDateTimeGenericZoneFormatter;

        return this.#formatPartsStripYearComma(formatter, date);
    }

    #formatPartsStripYearComma (formatter, date) {
        return formatter
            .formatToParts(date)
            .map((part, idx, parts) => {
                if (part.type === 'year' && parts[idx + 1]?.value.includes(',')) {
                    parts[idx + 1].value = ' ';
                }

                if (part.type === 'literal') {
                    return part.value.replace(/\u202f/g, ' ');
                }

                return part.value;
            })
            .join('');
    }

    formatFullDate ({ date, ignoreTimeZone = false }) {
        if (ignoreTimeZone) {
            return new Intl.DateTimeFormat(this.#locale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            }).format(date);
        }

        return this.#fullDateFormatter.format(date);
    }

    formatShortWithWeekday ({ date, ignoreTimeZone = false }) {
        let formatter = this.#shortWithWeekdayFormatter;
        if (ignoreTimeZone) {
            formatter = new Intl.DateTimeFormat(this.#locale, {
                weekday: 'long',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        }

        if (this.#locale === 'ja') {
            const parts = formatter.formatToParts(date);

            return parts
                .map((part) => {
                    if (part.type === 'weekday') {
                        return `・${part.value}`;
                    }

                    return part.value;
                })
                .join('');
        }

        return formatter.format(date);
    }

    formatTimeWithSeconds ({ date, ignoreTimeZone = false }) {
        if (ignoreTimeZone) {
            return new Intl.DateTimeFormat(this.#locale, {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
            }).format(date);
        }

        return this.#timeWithSecondsFormatter.format(date);
    }

    formatTimestampLabel ({ date = new Date() } = {}) {
        const datePart = new Intl.DateTimeFormat(this.#locale, {
            month: 'short',
            day: 'numeric',
            timeZone: this.#timeZone,
        }).format(date);

        const timePart = new Intl.DateTimeFormat(this.#locale, {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: this.#timeZone,
        })
            .formatToParts(date)
            .map((part) => (part.type === 'literal' ? part.value.replace(/\u202f/g, ' ') : part.value))
            .join('');

        return `${datePart} @ ${timePart}`;
    }

    formatBrowserTimezoneName ({ includeAbbr = false }) {
        const date = new Date();

        const longName = new Intl.DateTimeFormat(this.#locale, {
            timeZoneName: 'long',
        })
            .formatToParts(date)
            .map((part) => {
                if (part.type === 'timeZoneName') {
                    return part.value;
                }

                return '';
            })
            .join('');

        if (!includeAbbr) {
            return longName;
        }

        const abbr = new Intl.DateTimeFormat(this.#locale, {
            timeZoneName: 'short',
        })
            .formatToParts(date)
            .map((part) => {
                if (part.type === 'timeZoneName') {
                    return part.value;
                }

                return '';
            })
            .join('');

        return `${longName} (${abbr})`;
    }
}

const i18nDateTimeFormatter = new DateTimeFormatter();

export default i18nDateTimeFormatter;
